import { request } from './request';
import {
  catchError,
  combineLatest,
  EMPTY,
  filter,
  map,
  startWith,
  switchMap,
  take,
  tap,
  throttleTime,
  throwError,
} from 'rxjs';
import { queueNotice } from '../components/Notice';
import { bind } from '@react-rxjs/core';
import { convertToAsyncTaskState } from '../utils/rxjs/task';
import { createSignal } from '@react-rxjs/utils';
import { DateRange } from '../types';
import dayjs from 'dayjs';
import { Vehicle } from './vehicle';
import {
  dialog$,
  editedReservation$,
  setReserveDialog,
} from '../components/ReserveModal/service';

export type ReservationStatus = 'RESERVED' | 'IN_PROGRESS' | 'COMPLETED';

export interface ReservationDescriptor {
  id: number;
  clientId: number;
  objectId: number;
  objectName: string;
  driverId: number;
  status: ReservationStatus;
  plannedStartDateTime: string;
  plannedEndDateTime: string;
}

const BOOKINGS_PATH = '/gateway/vehicle-booking-service/v20261022/bookings';

export const isEditable = ({ status }: ReservationDescriptor) =>
  status === 'RESERVED' || status === 'IN_PROGRESS';

export const [range$, setRange] = createSignal<DateRange>();

const getInitialDateRange = (
  reservation: ReservationDescriptor | null
): DateRange =>
  reservation
    ? [reservation.plannedStartDateTime, reservation.plannedEndDateTime]
    : [
        dayjs().startOf('hour').add(1, 'h').toISOString(),
        dayjs().endOf('day').toISOString(),
      ];

const validateRange = (
  [from, to]: DateRange,
  allowPastStart: boolean
): string | null => {
  if (!from || !to) {
    return 'Date.Range.Error.Required';
  }

  if (!allowPastStart && dayjs(from).isBefore(dayjs(), 'minute')) {
    return 'Date.Range.Error.PastStart';
  }

  if (from > to) {
    return 'Date.Range.Error.Validity';
  }

  if (Math.abs(dayjs(from).diff(to, 'days')) > 31) {
    return 'Date.Range.Error.Exceeded';
  }

  return null;
};

export const [useDateRangeValidation, dateRangeValidation$] = bind(
  dialog$.pipe(
    filter((open) => open),
    switchMap(() => editedReservation$.pipe(take(1))),
    switchMap((reservation) =>
      range$.pipe(
        map((dateRange) => ({
          dateRange,
          error: validateRange(
            dateRange,
            reservation?.status === 'IN_PROGRESS'
          ),
        })),
        startWith({ dateRange: getInitialDateRange(reservation), error: null })
      )
    )
  )
);

const reservations$ = request<ReservationDescriptor[]>(BOOKINGS_PATH);

export const [reserveVehicleSignal$, reserveVehicle] = createSignal<number>();
export const [updateReservationSignal$, updateReservation] =
  createSignal<number>();
export const [deleteReservationSignal$, deleteReservationSignal] =
  createSignal<number>();

export const reserved$ = dateRangeValidation$.pipe(
  switchMap(({ dateRange: [plannedStartDateTime, plannedEndDateTime] }) =>
    reserveVehicleSignal$.pipe(
      switchMap((objectId) =>
        request(BOOKINGS_PATH, {
          method: 'POST',
          body: JSON.stringify({
            objectId,
            plannedStartDateTime,
            plannedEndDateTime,
          }),
          headers: { 'Content-Type': 'application/json' },
        }).pipe(
          catchError(() => {
            queueNotice({ message: 'Vehicle.Reserve.Error' });
            setReserveDialog(false);
            return EMPTY;
          }),
          tap(() => {
            queueNotice({
              message: 'Vehicle.Reserve.Success',
              variant: 'success',
              timer: 3000,
            });
            setReserveDialog(false);
          })
        )
      )
    )
  )
);

export const updated$ = combineLatest([
  dateRangeValidation$,
  editedReservation$,
]).pipe(
  switchMap(
    ([{ dateRange: [plannedStartDateTime, plannedEndDateTime] }, reservation]) =>
      updateReservationSignal$.pipe(
        filter(() => !!reservation),
        switchMap((objectId) =>
          request(BOOKINGS_PATH, {
            method: 'PUT',
            body: JSON.stringify({
              id: reservation!.id,
              objectId,
              plannedStartDateTime,
              plannedEndDateTime,
            }),
            headers: { 'Content-Type': 'application/json' },
          }).pipe(
            catchError(() => {
              queueNotice({ message: 'Vehicle.EditReserve.Error' });
              setReserveDialog(false);
              return EMPTY;
            }),
            tap(() => {
              queueNotice({
                message: 'Vehicle.EditReserve.Success',
                variant: 'success',
                timer: 3000,
              });
              setReserveDialog(false);
            })
          )
        )
      )
  )
);

export const canceled$ = deleteReservationSignal$.pipe(
  switchMap((id) =>
    request(`${BOOKINGS_PATH}/${id}`, {
      method: 'DELETE',
    }).pipe(
      catchError(() => {
        queueNotice({ message: 'Vehicle.CancelReserve.Error' });
        return EMPTY;
      }),
      tap(() => {
        queueNotice({
          message: 'Vehicle.CancelReserve.Success',
          variant: 'success',
          timer: 3000,
        });
      })
    )
  )
);

export const [useCancelReservation, canceledReservation$] = bind(
  canceled$,
  null
);

export const [useReserveVehicle, reservedVehicle$] = bind(reserved$, null);

export const [useUpdateReservation, updatedReservation$] = bind(updated$, null);

export const [useReservations] = bind(
  combineLatest([
    canceledReservation$,
    reservedVehicle$,
    updatedReservation$,
  ]).pipe(switchMap(() => reservations$.pipe(convertToAsyncTaskState()))),
  {
    result: null,
    running: true,
    error: null,
  }
);

export const [useAvailableVehicles] = bind(
  dateRangeValidation$.pipe(
    filter(({ error }) => !error),
    throttleTime(300, undefined, { leading: false, trailing: true }),
    switchMap(({ dateRange: [fromDatetime, toDatetime] }) =>
      editedReservation$.pipe(
        take(1),
        switchMap((reservation) =>
          request<Vehicle[]>(
            `${BOOKINGS_PATH}/available-objects?fromDatetime=${fromDatetime}&toDatetime=${toDatetime}${
              reservation ? `&bookingId=${reservation.id}` : ''
            }`
          ).pipe(
            catchError((e) => {
              queueNotice({
                message: 'Vehicle.Get.Error',
              });
              setReserveDialog(false);
              return throwError(() => e);
            }),
            convertToAsyncTaskState()
          )
        )
      )
    )
  ),
  {
    result: null,
    running: true,
    error: null,
  }
);
